/* =========================================================================
   Easy Explainers — gedeelde JS voor hero / player / grid sections.
   Werkt op document-niveau: vindt de data in [data-ep-videos] (grid section)
   en bedient het grid, related videos en de featured player. Geen module/scope
   nodig — een idempotente init() draait bij DOMContentLoaded én bij
   Shopify section-reload events.
   ========================================================================= */
(function () {
  'use strict';

  const STATE = {
    videos: [],
    currentSlug: null,
    currentAudience: 'all',
    currentSearch: '',
    initialized: false,
  };

  function readVideos() {
    const node = document.querySelector('[data-ep-videos]');
    if (!node) return [];
    try {
      return JSON.parse(node.textContent || '[]')
        .map(v => ({
          ...v,
          audiences: (v.audiences || []).map(a => String(a).trim()).filter(Boolean),
        }))
        .filter(v => v.title);
    } catch (e) {
      console.warn('[Easy Explainers] Kon video JSON niet lezen', e);
      return [];
    }
  }

  function svgPlay() {
    return '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>';
  }

  function svgArrow() {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>';
  }

  function buildThumb(v, showDur) {
    const ytThumb = v.youtubeId ? `https://i.ytimg.com/vi/${v.youtubeId}/hqdefault.jpg` : '';
    const src = v.poster || ytThumb;
    const thumbInner = src
      ? `<img src="${src}" alt="" loading="lazy" onerror="this.style.display='none'">`
      : `<div class="ep-card__thumb-fallback">
           <span class="ep-card__thumb-fallback-mark">EasyPets</span>
           <span class="ep-card__thumb-fallback-title">${(v.title || '').replace(/^Easy /, '')}</span>
         </div>`;
    return `
      <div class="ep-card__thumb">
        ${thumbInner}
        <div class="ep-card__play">
          <span class="ep-card__play-circle">${svgPlay()}</span>
        </div>
        ${showDur && v.duration ? `<span class="ep-card__duration">${v.duration}</span>` : ''}
      </div>`;
  }

  function showDuration() {
    const gridSection = document.querySelector('[data-ep-grid-section]');
    return !gridSection || gridSection.dataset.showDuration !== 'false';
  }

  function renderGrid() {
    const grid = document.querySelector('[data-ep-grid]');
    if (!grid) return;
    const f = (STATE.currentSearch || '').trim().toLowerCase();
    const filtered = STATE.videos.filter(v =>
      (!f
        || (v.title || '').toLowerCase().includes(f)
        || (v.desc || '').toLowerCase().includes(f)
        || (v.chip || '').toLowerCase().includes(f))
      && (STATE.currentAudience === 'all' || (v.audiences || []).includes(STATE.currentAudience))
    );

    grid.innerHTML = '';
    if (filtered.length === 0) {
      grid.innerHTML = `<div class="ep-grid__empty">Geen video's gevonden${f ? ` voor "<strong>${f}</strong>"` : ''}.</div>`;
      return;
    }

    const showDur = showDuration();
    filtered.forEach(v => {
      const btn = document.createElement('button');
      btn.className = 'ep-card' + (v.slug === STATE.currentSlug ? ' is-active' : '');
      btn.type = 'button';
      btn.dataset.slug = v.slug;
      btn.setAttribute('aria-label', `Speel af: ${v.title}`);
      btn.innerHTML = `
        ${buildThumb(v, showDur)}
        <div class="ep-card__body">
          <h3 class="ep-card__title">${v.title}</h3>
          <p class="ep-card__desc">${v.desc || ''}</p>
        </div>
      `;
      btn.addEventListener('click', () => playVideo(v.slug, true));
      grid.appendChild(btn);
    });
  }

  function renderRelated() {
    const related = document.querySelector('[data-ep-related]');
    if (!related) return;
    related.innerHTML = '';
    if (!STATE.videos.length) return;

    const current = STATE.videos.find(v => v.slug === STATE.currentSlug);
    let pool = STATE.videos.filter(v => v.slug !== STATE.currentSlug);
    if (current) {
      const same = pool.filter(v => v.chip === current.chip);
      const others = pool.filter(v => v.chip !== current.chip);
      pool = [...same, ...others];
    }
    const picks = pool.slice(0, 2);
    const showDur = showDuration();

    picks.forEach(v => {
      const btn = document.createElement('button');
      btn.className = 'ep-card';
      btn.type = 'button';
      btn.dataset.slug = v.slug;
      btn.setAttribute('aria-label', `Speel af: ${v.title}`);
      btn.innerHTML = `
        ${buildThumb(v, showDur)}
        <div class="ep-card__body">
          <h3 class="ep-card__title">${v.title}</h3>
          <p class="ep-card__desc">${v.desc || ''}</p>
          <a href="${v.pdpUrl || '#'}" class="ep-card__shop" data-ep-stop>${'Shop nu'} ${svgArrow()}</a>
        </div>
      `;
      btn.addEventListener('click', (e) => {
        if (e.target.closest('[data-ep-stop]')) return;
        playVideo(v.slug, true);
      });
      related.appendChild(btn);
    });
  }

  function playVideo(slug, scroll) {
    const video = STATE.videos.find(v => v.slug === slug);
    if (!video) return;
    STATE.currentSlug = slug;

    const frame = document.querySelector('[data-ep-player-frame]');
    const title = document.querySelector('[data-ep-featured-title]');
    const desc = document.querySelector('[data-ep-featured-desc]');
    const chips = document.querySelector('[data-ep-featured-chips]');
    const shop = document.querySelector('[data-ep-featured-shop]');
    const player = document.querySelector('[data-ep-player]');

    if (frame) {
      if (video.youtubeId) {
        const origin = encodeURIComponent(window.location.origin);
        frame.innerHTML = `<iframe src="https://www.youtube.com/embed/${video.youtubeId}?autoplay=1&rel=0&modestbranding=1&playsinline=1&origin=${origin}" title="${video.title}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>`;
      } else {
        frame.innerHTML = `
          <div class="ep-player__placeholder" style="gap:0.6rem;">
            <div style="width:84px;height:84px;border-radius:50%;background:rgba(255,255,255,0.95);color:var(--ep-energiek);display:grid;place-items:center;box-shadow:0 12px 30px rgba(0,0,0,0.3);">
              <svg viewBox="0 0 24 24" fill="currentColor" style="width:32px;height:32px;margin-left:4px;"><path d="M8 5v14l11-7z"/></svg>
            </div>
            <p style="font-family:var(--ep-font-heading);font-size:1.5rem;color:#fff;margin:0;">${video.title}</p>
            <p style="font-family:var(--ep-font-sub);font-size:0.9rem;opacity:0.7;color:#fff;margin:0;">Video volgt binnenkort.</p>
          </div>`;
      }
    }

    if (title) title.textContent = video.title;
    if (desc) desc.textContent = video.desc || '';
    if (chips) {
      chips.innerHTML = `
        <span class="ep-featured-meta__chip ep-featured-meta__chip--accent">${video.chip || 'Easy Explainer'}</span>
        ${video.duration ? `<span class="ep-featured-meta__chip">⏱ ${video.duration}</span>` : ''}
      `;
    }
    if (shop) {
      shop.setAttribute('href', video.pdpUrl || '#');
      shop.classList.add('is-visible');
    }

    const url = new URL(window.location.href);
    url.searchParams.set('v', slug);
    url.hash = '';
    history.replaceState({}, '', url);

    renderRelated();
    document.querySelectorAll('.ep-card').forEach(c => {
      c.classList.toggle('is-active', c.dataset.slug === slug);
    });

    if (scroll && player) {
      player.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  function resolveDeepLink() {
    const url = new URL(window.location.href);
    const param = url.searchParams.get('v');
    const hash = (url.hash || '').replace('#', '');
    const slug = param || hash;
    if (slug && STATE.videos.find(v => v.slug === slug)) {
      playVideo(slug, false);
    }
  }

  function bindControls() {
    const placeholderPlay = document.querySelector('[data-ep-placeholder-play]');
    if (placeholderPlay && !placeholderPlay.dataset.epBound) {
      placeholderPlay.dataset.epBound = '1';
      placeholderPlay.addEventListener('click', () => {
        if (STATE.videos.length) playVideo(STATE.videos[0].slug, false);
      });
    }

    const shareBtn = document.querySelector('[data-ep-share]');
    if (shareBtn && !shareBtn.dataset.epBound) {
      shareBtn.dataset.epBound = '1';
      const label = shareBtn.querySelector('[data-ep-share-label]');
      shareBtn.addEventListener('click', async () => {
        const url = new URL(window.location.href);
        if (STATE.currentSlug) url.searchParams.set('v', STATE.currentSlug);
        try {
          await navigator.clipboard.writeText(url.toString());
          if (label) label.textContent = 'Link gekopieerd!';
          shareBtn.classList.add('is-copied');
          setTimeout(() => {
            if (label) label.textContent = 'Link delen';
            shareBtn.classList.remove('is-copied');
          }, 2000);
        } catch (_) {
          window.prompt('Kopieer deze link:', url.toString());
        }
      });
    }

    const search = document.querySelector('[data-ep-search]');
    if (search && !search.dataset.epBound) {
      search.dataset.epBound = '1';
      search.addEventListener('input', (e) => {
        STATE.currentSearch = e.target.value;
        renderGrid();
      });
    }

    document.querySelectorAll('[data-ep-filters] .ep-filter-btn').forEach(btn => {
      if (btn.dataset.epBound) return;
      btn.dataset.epBound = '1';
      btn.addEventListener('click', () => {
        document.querySelectorAll('[data-ep-filters] .ep-filter-btn').forEach(b => b.classList.remove('is-active'));
        btn.classList.add('is-active');
        STATE.currentAudience = btn.dataset.audience;
        renderGrid();
      });
    });
  }

  function init() {
    STATE.videos = readVideos();
    renderGrid();
    renderRelated();
    bindControls();
    if (!STATE.initialized) {
      resolveDeepLink();
      window.addEventListener('popstate', resolveDeepLink);
      STATE.initialized = true;
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify theme editor: re-init bij section reload
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:reorder', init);
  document.addEventListener('shopify:block:select', init);
})();
